// [AI_START::module id="feature-flags/useEvaluateFeatureFlags.ts" purpose="Live flags from GraphQL subscription payloads; HTTP query or Lambda poll only while subscription is not live" links=["fetchFeatureFlagsFallback", "findFeatureFlag", "graphql_feature_flags"]]
import { useAuth0 } from "@auth0/auth0-react";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { fetchFeatureFlagsFallback } from "~@/feature-flags/fallback";
import {
  EvaluateFeatureFlagsState,
  FeatureFlagResult,
  FeatureFlagTrait,
} from "~@/feature-flags/types";
import {
  useEvaluateFeatureFlagsQuery,
  useOnFeatureFlagsChangedSubscription,
} from "~@/graphql/codegen/generated";

/** Poll interval for GraphQL query or Lambda while the subscription is not live. */
const RECOVERY_POLL_MS = 10_000;

// [AI_START::function id="mapGraphqlFlags" purpose="Normalize GraphQL FeatureFlagEvaluation rows into FeatureFlagResult" links=["useEvaluateFeatureFlags"]]
function mapGraphqlFlags(
  rows: Array<{ flagId: string; enabled: boolean; value?: string | null }>
): FeatureFlagResult[] {
  return rows.map((flag) => ({
    flagId: flag.flagId,
    enabled: flag.enabled,
    value: flag.value ?? null,
  }));
}
// [AI_END::mapGraphqlFlags]

// [AI_START::function id="sameFlagResults" purpose="Avoid React update loops when Apollo/query returns equivalent evaluations" links=["useEvaluateFeatureFlags"]]
function sameFlagResults(
  left: FeatureFlagResult[] | null,
  right: FeatureFlagResult[]
): boolean {
  if (!left || left.length !== right.length) {
    return false;
  }
  return left.every(
    (flag, index) =>
      flag.flagId === right[index].flagId &&
      flag.enabled === right[index].enabled &&
      flag.value === right[index].value
  );
}
// [AI_END::sameFlagResults]

// [AI_START::function id="applyFlags" purpose="Write evaluations into lastFlags only when the snapshot actually changed" links=["sameFlagResults", "useEvaluateFeatureFlags"]]
function applyFlags(
  setLastFlags: Dispatch<SetStateAction<FeatureFlagResult[] | null>>,
  rows: Array<{ flagId: string; enabled: boolean; value?: string | null }>
): void {
  const next = mapGraphqlFlags(rows);
  setLastFlags((prev) => (sameFlagResults(prev, next) ? prev : next));
}
// [AI_END::applyFlags]

// [AI_START::function id="useEvaluateFeatureFlags" purpose="Prefer live subscription snapshots; poll query then Lambda only when subscription is not live" links=["fetchFeatureFlagsFallback", "findFeatureFlag"]]
export function useEvaluateFeatureFlags(
  flagIds: string[],
  traits?: FeatureFlagTrait[]
): EvaluateFeatureFlagsState {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const [lastFlags, setLastFlags] = useState<FeatureFlagResult[] | null>(null);
  // Live = at least one successful subscription payload since last error/complete.
  const [subscriptionLive, setSubscriptionLive] = useState(false);
  // Brief skip after error/complete so Apollo remounts the subscription.
  const [subscriptionPaused, setSubscriptionPaused] = useState(false);

  const getToken = useCallback(async () => {
    return getAccessTokenSilently();
  }, [getAccessTokenSilently]);

  const skipAuth = !isAuthenticated || !flagIds.length;
  const flagIdsKey = flagIds.join("\0");
  const traitsKey = JSON.stringify(traits ?? null);

  const subscriptionSkip = skipAuth || subscriptionPaused;
  // HTTP query only while subscription is not feeding us.
  const querySkip = skipAuth || subscriptionLive;

  useOnFeatureFlagsChangedSubscription({
    skip: subscriptionSkip,
    variables: { flagIds, traits },
    onData: ({ data: result }) => {
      const rows = result.data?.onFeatureFlagsChanged;
      if (!rows) {
        return;
      }
      applyFlags(setLastFlags, rows);
      setSubscriptionLive(true);
    },
    onError: () => {
      setSubscriptionLive(false);
      setSubscriptionPaused(true);
    },
    onComplete: () => {
      // Gate may end the stream after the first yield when Valkey is not ready.
      setSubscriptionLive(false);
      setSubscriptionPaused(true);
    },
  });

  const { data, error } = useEvaluateFeatureFlagsQuery({
    skip: querySkip,
    variables: { flagIds, traits },
    fetchPolicy: "network-only",
    pollInterval: querySkip ? 0 : RECOVERY_POLL_MS,
    notifyOnNetworkStatusChange: true,
  });

  // Apply successful query polls while subscription is down.
  useEffect(() => {
    if (querySkip || error || !data?.evaluateFeatureFlags) {
      return;
    }
    applyFlags(setLastFlags, data.evaluateFeatureFlags);
  }, [querySkip, error, data?.evaluateFeatureFlags]);

  // Reset when the requested flag set changes.
  useEffect(() => {
    setLastFlags(null);
    setSubscriptionLive(false);
    setSubscriptionPaused(false);
  }, [flagIdsKey]);

  // Retry subscription after pause (error or complete).
  useEffect(() => {
    if (skipAuth || !subscriptionPaused) {
      return;
    }
    const timerId = window.setTimeout(() => {
      setSubscriptionPaused(false);
    }, RECOVERY_POLL_MS);
    return () => {
      window.clearTimeout(timerId);
    };
  }, [subscriptionPaused, skipAuth]);

  // Lambda poll only when subscription is not live and the GraphQL query is failing.
  const useLambdaPoll = !skipAuth && !subscriptionLive && !!error;

  useEffect(() => {
    if (!useLambdaPoll) {
      return;
    }
    let cancelled = false;
    let inFlight = false;
    const tick = async () => {
      if (cancelled || inFlight) {
        return;
      }
      inFlight = true;
      try {
        const flags = await fetchFeatureFlagsFallback(flagIds, getToken, traits);
        if (!cancelled) {
          setLastFlags((prev) => (sameFlagResults(prev, flags) ? prev : flags));
        }
      } catch {
        if (!cancelled) {
          setLastFlags(
            (prev) =>
              prev ??
              flagIds.map((flagId) => ({
                flagId,
                enabled: false,
                value: null,
              }))
          );
        }
      } finally {
        inFlight = false;
      }
    };
    tick();
    const id = window.setInterval(tick, RECOVERY_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional stable keys
  }, [useLambdaPoll, flagIdsKey, traitsKey, getToken]);

  return useMemo((): EvaluateFeatureFlagsState => {
    if (!flagIds.length) {
      return {
        flags: [],
        loading: false,
      };
    }
    // Block UI only until the first successful evaluation from any source.
    return {
      flags: lastFlags ?? [],
      loading: lastFlags === null && !skipAuth,
    };
  }, [flagIds.length, lastFlags, skipAuth]);
}
// [AI_END::useEvaluateFeatureFlags]
// [AI_END::feature-flags/useEvaluateFeatureFlags.ts]
